import React, { useState, useEffect, useCallback } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, CreditCard, Star } from "lucide-react";

const PROVIDERS = [
  { value: "bank_transfer", label: "Transferencia bancaria" },
  { value: "paypal", label: "PayPal" },
  { value: "mercado_pago", label: "Mercado Pago" },
  { value: "stripe", label: "Stripe" },
  { value: "cash", label: "Efectivo" },
  { value: "custom", label: "Personalizado" },
];

const EMPTY = {
  name: "", provider: "bank_transfer", instructions: "",
  account_info: "", currency: "USD", is_active: true, is_default: false,
};

export default function PaymentMethodsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const fetchItems = useCallback(async () => {
    try { const { data } = await authAxios.get(`${API}/payment-methods`); setItems(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const save = async () => {
    if (!form.name.trim()) { alert("Nombre requerido"); return; }
    if (editing) await authAxios.put(`${API}/payment-methods/${editing}`, form);
    else await authAxios.post(`${API}/payment-methods`, form);
    setOpen(false); fetchItems();
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar método de pago?")) return;
    await authAxios.delete(`${API}/payment-methods/${id}`); fetchItems();
  };

  const toggle = async (m) => {
    await authAxios.put(`${API}/payment-methods/${m.id}`, { ...m, is_active: !m.is_active });
    fetchItems();
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="payments-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <CreditCard className="w-7 h-7" /> Métodos de Pago
          </h1>
          <p className="text-slate-500 mt-1">Configura la info que el bot envía con el comando <code>.pago</code></p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="pm-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo Método
        </Button>
      </div>

      {loading ? <div className="animate-pulse h-40 bg-slate-200 rounded-xl" /> :
       items.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-500">
          <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
          Aún no hay métodos de pago configurados.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map(m => (
            <Card key={m.id} data-testid={`pm-card-${m.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{m.name}</h3>
                    {m.is_default && <Badge className="bg-amber-100 text-amber-800"><Star className="w-3 h-3 mr-1" />Por defecto</Badge>}
                    <Badge variant="secondary">{PROVIDERS.find(p => p.value === m.provider)?.label || m.provider}</Badge>
                  </div>
                  {m.account_info && <p className="text-sm font-mono bg-slate-50 p-2 rounded mt-1">{m.account_info}</p>}
                  {m.instructions && <p className="text-sm text-slate-500 mt-2 whitespace-pre-wrap">{m.instructions}</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Switch checked={!!m.is_active} onCheckedChange={() => toggle(m)} data-testid={`pm-toggle-${m.id}`} />
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(m.id); setForm({ ...EMPTY, ...m }); setOpen(true); }} data-testid={`pm-edit-${m.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(m.id)} data-testid={`pm-delete-${m.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Método" : "Nuevo Método de Pago"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej. BBVA Cuenta Principal" data-testid="pm-name" />
            </div>
            <div><Label>Proveedor</Label>
              <Select value={form.provider} onValueChange={v => setForm({ ...form, provider: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Datos de la cuenta</Label>
              <Input value={form.account_info} onChange={e => setForm({ ...form, account_info: e.target.value })} placeholder="Número de cuenta / email / link" data-testid="pm-account" />
            </div>
            <div><Label>Instrucciones</Label>
              <Textarea rows={3} value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })} placeholder="Ej. Enviar comprobante al WhatsApp del admin." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between">
                <Label>Activo</Label>
                <Switch checked={!!form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Por defecto</Label>
                <Switch checked={!!form.is_default} onCheckedChange={v => setForm({ ...form, is_default: v })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="pm-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
